<template>
  <div class="simulacrum-slot w-100 h-100 p-1 d-flex flex-column">
    <div v-if="loading" class="d-flex w-100 h-100 align-items-center justify-content-center">
      <b-spinner label="Loading" variant="primary" />
    </div>
    <div v-else class="w-100 d-flex flex-column min-h-0 flex-grow-1">
      <div class="d-flex align-items-center justify-content-between mb-1 slot-header">
        <h5 class="mb-0 text-capitalize">{{ $tc('simulacrum.title', 1) }}</h5>
        <b-badge pill variant="light-primary" v-if="currentState">
          {{ $t('custom_forms.state') || 'Estado' }}: {{ labelForState(currentState.toLowerCase()) }}
        </b-badge>
      </div>

      <div class="slot-body flex-grow-1 min-h-0 overflow-auto">
        <div v-if="currentSimulacrumId" class="mb-1">
          <div class="d-flex flex-column" style="gap: 12px;">
            <div class="info-card">
              <div class="info-row">
                <span class="info-label">{{ tf('risk.reference_number', 'Número de Referencia') }}:</span>
                <span class="info-value">{{ referenceNumber }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">{{ tc('unit.title', 1, 'Instalación') }}:</span>
                <span class="info-value">{{ unitNameDisplay }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">REMS:</span>
                <span class="info-value">{{ rems }}</span>
              </div>
              <div class="info-row">
                <span class="info-label">SIRH:</span>
                <span class="info-value">{{ sirh }}</span>
              </div>
              <div v-if="templateNames" class="info-row">
                <span class="info-label">{{ tf('risk.template.title', 'Plantilla') }}:</span>
                <span class="info-value">{{ templateNames }}</span>
              </div>
            </div>
            <div class="history-card">
              <div class="history-header d-flex align-items-center justify-content-between">
                <strong>{{ tf('custom_forms.history', 'Historial') }}</strong>
                <small class="text-muted">{{ sortedHistory.length }} {{ tf('custom_forms.entries', 'entradas') }}</small>
              </div>
              <div class="history-table" v-if="sortedHistory.length">
                <div class="history-row history-row--head" :style="historyGridStyle">
                  <div class="col-state">{{ tf('custom_forms.state', 'Estado') }}</div>
                  <div class="col-user">{{ tf('custom_forms.user', 'Usuario') }}</div>
                  <div class="col-date">{{ tf('custom_forms.date', 'Fecha') }}</div>
                  <div v-if="hasHistoryComments" class="col-note">
                    {{ tf('custom_forms.note', 'Nota') }}
                  </div>
                </div>
                <div
                  v-for="s in visibleHistory"
                  :key="s.id"
                  class="history-row"
                  :style="historyGridStyle"
                >
                  <div class="col-state">
                    <b-badge pill :variant="badgeFor(s.name)" class="history-badge">
                      {{ labelForState(s.name) }}
                    </b-badge>
                  </div>
                  <div class="col-user">
                    <div class="history-user" :title="formatUser(s)">
                      <div class="user-name">{{ getUserName(s) || '—' }}</div>
                      <div v-if="getUserEmail(s)" class="user-email">
                        {{ getUserEmail(s) }}
                      </div>
                    </div>
                  </div>
                  <div class="col-date">
                    <small class="history-date">{{ formatDate(s.created_at) }}</small>
                  </div>
                  <div v-if="hasHistoryComments" class="col-note">
                    <small class="text-muted" :title="getStatusComment(s)">{{ getStatusComment(s) || '—' }}</small>
                  </div>
                </div>
              </div>
              <div v-else class="text-muted small py-1">
                {{ tf('custom_forms.empty', 'Sin movimientos') }}
              </div>
              <div v-if="sortedHistory.length > limitHistory" class="text-right mt-1">
                <b-button
                  variant="link"
                  size="sm"
                  class="p-0"
                  @click="showAllHistory = !showAllHistory"
                >
                  {{
                    showAllHistory
                      ? tf('custom_forms.show_less', 'Ver menos')
                      : tf('custom_forms.show_all', 'Ver todo el historial')
                  }}
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <build-simulacrum-form
          v-if="currentSimulacrumId && readyToRenderForm"
          :key="runnerKey"
          ref="runner"
          :simulacrum-id="currentSimulacrumId"
          :template-snapshot="templateSnapshot"
          :template-source-ids="templateSourceIds"
          :current-state="currentState"
          :is-reviewer="canReviewSimulacrum"
          :can-edit="canEditForm"
          @submit="onAnswersSubmit"
          @submit-success="onAnswersSubmit"
          @submitError="onAnswersSubmitError"
        />

        <div v-if="currentSimulacrumId" class="mt-1">
          <div class="text-center mb-50">
            <b-button v-if="showSaveButton" :disabled="saving" variant="primary" class="mb-1" @click="onClickSave">
              {{ $t('actions.save') || tf('actions.save', 'Guardar') }}
            </b-button>
          </div>

          <template v-if="showReviewActions">
            <div class="d-flex flex-column flex-md-row mt-1 justify-content-center align-items-center text-center" style="gap:8px;">
              <b-button
                v-if="canReviewSimulacrum"
                variant="success"
                class="mb-1"
                @click="updateSimulacrumStatus('approved')"
              >
                {{ $t('actions.approve') }}
              </b-button>
              <b-button
                v-if="canReviewSimulacrum"
                variant="danger"
                class="mb-1"
                @click="updateSimulacrumStatus('rejected')"
              >
                {{ $t('actions.reject') }}
              </b-button>
            </div>
          </template>

          <template v-if="canSendToReview && !showReviewActions">
            <div class="d-flex flex-column flex-md-row mt-1 justify-content-center align-items-center text-center" style="gap:8px;">
              <div class="reviewer-inline mr-2 mb-1">
                <v-select
                  v-model="selectedReviewer"
                  :options="userOptions"
                  :reduce="u => u.value"
                  :clearable="false"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :placeholder="tf('custom_forms.select_reviewer', 'Selecciona usuario revisor')"
                  input-id="reviewer"
                  class="select-opens-up"
                />
              </div>
              <b-button
                variant="warning"
                class="mb-1"
                :disabled="!selectedReviewer || saving"
                @click="updateSimulacrumStatus('in_review', selectedReviewer)"
              >
                {{ tf('custom_forms.send_to_review', 'Enviar a revisión') }}
              </b-button>
            </div>
          </template>

          <template v-else-if="canReassignAfterRejected">
            <div class="d-flex flex-column flex-md-row mt-1 justify-content-center align-items-center text-center" style="gap:8px;">
              <div class="reviewer-inline mr-2 mb-1">
                <v-select
                  v-model="selectedReassign"
                  :options="reassignableOptions"
                  :reduce="u => u.value"
                  :clearable="false"
                  :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                  :placeholder="tf('custom_forms.select_reassigner', 'Selecciona usuario para reasignar')"
                  input-id="reassign-after-reject"
                  class="select-opens-up"
                />
              </div>
              <b-button
                variant="warning"
                class="mb-1"
                :disabled="!selectedReassign || saving"
                @click="updateSimulacrumStatus('reassigned', selectedReassign)"
              >
                {{ tf('custom_forms.reassign', 'Reasignar') }}
              </b-button>
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, getCurrentInstance } from '@vue/composition-api'
import axios from '@/libs/axios'
import BuildSimulacrumForm from '@/components/BuildSimulacrumForm'
import vSelect from 'vue-select'
import { BButton, BBadge, BSpinner } from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import ability from '@/libs/acl/ability'

export default {
  name: 'SimulacrumSlot',
  components: { BuildSimulacrumForm, vSelect, BButton, BBadge, BSpinner },
  props: {
    data: { type: Object, required: true },
  },
  setup(props) {
    const vm = getCurrentInstance().proxy
    const loading = ref(true)
    const saving = ref(false)
    const currentSimulacrumId = ref(null)
    const inlineSnapshot = ref(null)
    const inlineSources = ref(null)
    const referenceNumber = ref('')
    const unitNameDisplay = ref('')
    const rems = ref('')
    const sirh = ref('')
    const currentState = ref('in_progress')
    const templateNames = ref('')
    const statuses = ref([])
    const readyToRenderForm = ref(false)
    const runnerKey = ref(0)
    const showAllHistory = ref(false)
    const limitHistory = ref(6)
    const selectedReviewer = ref(null)
    const userOptions = ref([])
    const reassignableOptions = ref([])
    const selectedReassign = ref(null)
    const lastStatusUserId = ref(null)
    const didBootstrap = ref(false)
    const creating = ref(false)
    const templateSnapshot = ref([])
    const templateSourceIds = ref([])
    const _pendingSaveResolve = ref(null)

    const can = (action, subject) => {
      try {
        return typeof vm.$can === 'function' ? vm.$can(action, subject) : ability.can(action, subject)
      } catch (e) {
        return ability.can(action, subject)
      }
    }

    const getLocalUserId = () => {
      try {
        const raw = localStorage.getItem('user') || localStorage.getItem('userData') || ''
        if (!raw) return null
        const obj = JSON.parse(raw)
        const candidates = [obj?.id, obj?.user?.id, obj?.data?.id, obj?.user_id, obj?.uid]
        for (const c of candidates) {
          const n = Number(c)
          if (Number.isFinite(n) && n > 0) return n
        }
        return null
      } catch {
        return null
      }
    }

    const getLocalUserEmail = () => {
      try {
        const raw = localStorage.getItem('user') || localStorage.getItem('userData') || ''
        if (!raw) return null
        const obj = JSON.parse(raw)
        const candidates = [obj?.email, obj?.user?.email, obj?.data?.email]
        for (const c of candidates) {
          if (c && typeof c === 'string' && c.includes('@')) return c
        }
        return null
      } catch {
        return null
      }
    }

    const isAssignedToCurrentAuthorState = computed(() => {
      const st = String(currentState.value || '').toLowerCase()
      if (!['in_progress', 'reassigned'].includes(st)) return false

      let assignedId = lastStatusUserId.value
      let assignedEmail = null
      const list = Array.isArray(sortedHistory.value) ? sortedHistory.value : []

      const lastForState = list.find(s => String(s?.name || '').toLowerCase() === st)

      if (lastForState) {
        assignedId = assignedId || lastForState?.id_user || lastForState?.user_id || lastForState?.owner_id || lastForState?.id_owner || lastForState?.reviewer_id || lastForState?.id_reviewer || lastForState?.user?.id || lastForState?.userId || null
        assignedEmail = lastForState?.user?.email || lastForState?.email || null
      }

      const currentUserId = getLocalUserId()
      const currentUserEmail = getLocalUserEmail()

      const idMatch = Number(assignedId) > 0 && Number(currentUserId) > 0 && Number(assignedId) === Number(currentUserId)
      const emailMatch = assignedEmail && currentUserEmail && String(assignedEmail).toLowerCase() === String(currentUserEmail).toLowerCase()

      return !!(idMatch || emailMatch)
    })

    const canReviewSimulacrum = computed(() => {
      if (!can('review_simulacrum', 'Simulacrum')) return false
      const st = String(currentState.value || '').toLowerCase()
      if (st !== 'in_review') return false

      let assignedId = lastStatusUserId.value
      let assignedEmail = null
      const list = Array.isArray(sortedHistory.value) ? sortedHistory.value : []
      const lastReview = list.find(s => String(s?.name || '').toLowerCase() === 'in_review')

      if (lastReview) {
        assignedId = assignedId || lastReview?.id_user || lastReview?.user_id || lastReview?.reviewer_id || lastReview?.id_reviewer || lastReview?.user?.id || lastReview?.userId || null
        assignedEmail = lastReview?.user?.email || lastReview?.email || null
      }

      const currentUserId = getLocalUserId()
      const currentUserEmail = getLocalUserEmail()
      const idMatch = Number(assignedId) > 0 && Number(currentUserId) > 0 && Number(assignedId) === Number(currentUserId)
      const emailMatch = assignedEmail && currentUserEmail && String(assignedEmail).toLowerCase() === String(currentUserEmail).toLowerCase()

      return !!(idMatch || emailMatch)
    })

    const canEditForm = computed(() => {
      const state = String(currentState.value || '').toLowerCase()

      if (['approved', 'rejected', 'not_approved', 'scheduled', 'canceled'].includes(state)) {
        return false
      }

      if (state === 'in_review') {
        return false
      }

      if (state === 'in_progress' || state === 'reassigned') {
        return isAssignedToCurrentAuthorState.value
      }

      return false
    })

    const hasSpecialReasingForSimulacrum = computed(() => {
      try {
        return !!(vm.$can && vm.$can('special_reasing', 'Simulacrum'))
      } catch (_) {
        return false
      }
    })

    const canSendToReview = computed(() => {
      const st = String(currentState.value || '').toLowerCase()
      const finalStates = ['approved', 'rejected', 'not_approved', 'scheduled', 'canceled']
      if (finalStates.includes(st)) return false

      if (st === 'in_progress' || st === 'reassigned') {
        return (
          isAssignedToCurrentAuthorState.value ||
          hasSpecialReasingForSimulacrum.value
        )
      }

      if (st === 'in_review') {
        return hasSpecialReasingForSimulacrum.value
      }

      return hasSpecialReasingForSimulacrum.value
    })

    const showReviewActions = computed(() => {
      return String(currentState.value || '').toLowerCase() === 'in_review'
    })

    const canReassignAfterRejected = computed(() => {
      const st = String(currentState.value || '').toLowerCase()
      return st === 'rejected' && can('review_simulacrum', 'Simulacrum')
    })

    const canShowReassign = computed(() => {
      const st = String(currentState.value || '').toLowerCase()
      return st !== 'approved' && st !== 'rejected'
    })

    const showSaveButton = computed(() => {
      const state = String(currentState.value || '').toLowerCase()

      if (state === 'approved' || state === 'rejected' || state === 'canceled') {
        return false
      }

      if (state === 'in_review') {
        let assignedId = lastStatusUserId.value
        let assignedEmail = null

        const list = Array.isArray(sortedHistory.value) ? sortedHistory.value : []
        const lastReview = list.find(s => String(s?.name || '').toLowerCase() === 'in_review')

        if (lastReview) {
          assignedId = assignedId || lastReview?.id_user || lastReview?.user_id || lastReview?.reviewer_id || lastReview?.id_reviewer || lastReview?.user?.id || lastReview?.userId || null
          assignedEmail = lastReview?.user?.email || lastReview?.email || null
        }

        const currentUserId = getLocalUserId()
        const currentUserEmail = getLocalUserEmail()

        if (!assignedId && !assignedEmail) return false
        if (!currentUserId && !currentUserEmail) return false

        const idMatch = Number(assignedId) > 0 && Number(currentUserId) > 0 && Number(assignedId) === Number(currentUserId)
        const emailMatch = assignedEmail && currentUserEmail && String(assignedEmail).toLowerCase() === String(currentUserEmail).toLowerCase()

        return !!(idMatch || emailMatch)
      }

      if (state === 'in_progress' || state === 'reassigned') {
        return isAssignedToCurrentAuthorState.value
      }

      return false
    })

    const sortedHistory = computed(() => {
      return [...statuses.value].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    })

    const visibleHistory = computed(() => {
      return showAllHistory.value ? sortedHistory.value : sortedHistory.value.slice(0, limitHistory.value)
    })

    const hasHistoryComments = computed(() => {
      return statuses.value.some(s => !!getStatusComment(s))
    })

    const historyGridStyle = computed(() => {
      const base = 'max-content minmax(140px, 1fr) minmax(190px, max-content)'
      return {
        gridTemplateColumns: hasHistoryComments.value ? `${base} minmax(140px, 1fr)` : base,
      }
    })

    const humanize = (str = '') => String(str).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())

    const labelForState = (name) => {
      const raw = String(name || '')
      const norm = raw.trim().toLowerCase().replace(/[-\s]+/g, '_')
      const aliases = { canceled: 'cancelled' }
      const canonical = aliases[norm] || norm
      const tryKeys = [`procedure_states.${canonical}`, `procedure_states.value.${canonical}`]
      for (const k of tryKeys) {
        const v = vm.$t ? vm.$t(k) : k
        if (typeof v === 'string' && v !== k) return v
      }
      return humanize(canonical)
    }

    const badgeFor = (name) => {
      const map = {
        in_progress: 'primary',
        in_review: 'warning',
        approved: 'success',
        rejected: 'danger',
        scheduled: 'info',
        canceled: 'canceled',
        reassigned: 'reassigned',
      }
      const key = String(name || '').toLowerCase()
      return map[key] || 'secondary'
    }

    const formatDate = (iso) => {
      if (!iso) return ''
      try {
        const d = new Date(iso)
        return d.toLocaleString()
      } catch {
        return iso
      }
    }

    const getUserName = (s) => s?.user_name || s?.user?.name || ''
    const getUserEmail = (s) => {
      const u = s?.user
      if (!u) return ''
      if (typeof u === 'string') return u
      return u?.email || ''
    }
    const formatUser = (s) => {
      const name = getUserName(s)
      const email = getUserEmail(s)
      return [name, email].filter(Boolean).join(' | ')
    }
    const getStatusComment = (s) => s?.comment || s?.comments || s?.note || s?.notes || s?.description || ''

    const tf = (key, fallback = '') => {
      try {
        const out = vm.$t ? vm.$t(key) : key
        return typeof out === 'string' && out === key ? fallback : out
      } catch {
        return fallback
      }
    }

    const tc = (key, count = 1, fallback = '') => {
      try {
        const out = vm.$tc ? vm.$tc(key, count) : key
        return typeof out === 'string' && out === key ? fallback : out
      } catch {
        return fallback
      }
    }

    const resolveTemplateSourcesByIds = async (ids = []) => {
      if (!Array.isArray(ids) || !ids.length) return []
      try {
        const { data } = await axios.get('/v1/simulacrum_templates')
        const all = data.data || []
        const set = new Set(ids.map(Number))
        return all.filter(t => set.has(Number(t.id))).map(t => ({ id: t.id, name: t.name }))
      } catch {
        return []
      }
    }

    const loadSimulacrumMeta = async (id) => {
      try {
        const { data } = await axios.get(`/v1/simulacrums/${id}`)
        const ev = data && data.data ? data.data : data
        referenceNumber.value = ev.reference_number || ''
        unitNameDisplay.value = ev.unit?.description || ''
        rems.value = ev.rems ?? ev.unit?.rems ?? ''
        sirh.value = ev.sirh ?? ev.unit?.sirh ?? ''
        currentState.value = ev.last_status?.name || 'in_progress'
        statuses.value = Array.isArray(ev.statuses) ? ev.statuses : []
        lastStatusUserId.value = ev.last_status?.id_user || ev.last_status?.user_id || ev.last_status?.reviewer_id || ev.last_status?.id_reviewer || ev.last_status?.user?.id || null

        if (String(currentState.value).toLowerCase() === 'rejected') {
          loadReassignableOptions()
        } else {
          selectedReassign.value = null
        }

        if (ev.formAnswer?.template_sources?.length) {
          templateNames.value = ev.formAnswer.template_sources.map(s => (s && s.name ? s.name : null)).filter(Boolean).join(', ')
        } else if (templateSourceIds.value?.length) {
          const srcObjs = await resolveTemplateSourcesByIds(templateSourceIds.value)
          templateNames.value = srcObjs.map(s => s.name).join(', ')
        } else {
          templateNames.value = ''
        }
      } catch (e) {
      }
    }

    const ensureAnswerExists = async () => {
      if (didBootstrap.value || creating.value) return
      if (!currentSimulacrumId.value) return
      try {
        await axios.get(`/v1/simulacrum_form_answers/get-form-by-simulacrum/${currentSimulacrumId.value}`)
        didBootstrap.value = true
      } catch (e) {
        creating.value = true
        try {
          const formData = new FormData()
          formData.append('id_simulacrum', currentSimulacrumId.value)
          formData.append('data', JSON.stringify({}))
          formData.append('template_snapshot', JSON.stringify(templateSnapshot.value || []))
          let sourcesToSend = []
          if (templateSourceIds.value && templateSourceIds.value.length) {
            sourcesToSend = await resolveTemplateSourcesByIds(templateSourceIds.value)
          }
          formData.append('template_sources', JSON.stringify(sourcesToSend))
          await axios.post('/v1/simulacrum_form_answers', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          if (sourcesToSend.length) {
            templateNames.value = sourcesToSend.map(s => s.name).join(', ')
          } else if (templateSnapshot.value?.length) {
            templateNames.value = tf('risk.template.composed', 'Composed')
          }
          didBootstrap.value = true
          await loadSimulacrumMeta(currentSimulacrumId.value)
        } catch (_) {
        } finally {
          creating.value = false
        }
      }
    }

    const loadReviewerOptions = async () => {
      const mapUsers = (arr) => (arr || []).map(u => ({ value: u.id, label: `${u.name}| ${u.email}` }))
      try {
        const { data } = await axios.get('/v1/simulacrums/getReviewers')
        const list = data?.data || data || []
        if (Array.isArray(list) && list.length) {
          userOptions.value = mapUsers(list)
          return
        }
      } catch (_) {
      }
      try {
        const { data } = await axios.get('/v1/risk_evaluations/getReviewers')
        const list = data?.data || data || []
        userOptions.value = mapUsers(list)
      } catch (_) {
        userOptions.value = []
      }
    }

    const loadReassignableOptions = async () => {
      try {
        const { data } = await axios.get('/v1/simulacrums/getReassignable')
        const list = data?.data || data || []
        reassignableOptions.value = (list || []).map(u => ({
          value: u.id,
          label: `${u.name}| ${u.email}`,
        }))
      } catch (_) {
        reassignableOptions.value = []
      }
    }

    const updateSimulacrumStatus = async (newStatus, toUserId = null) => {
      const guardedStatuses = ['approved', 'rejected']
      if (guardedStatuses.includes(newStatus) && !canReviewSimulacrum.value) return
      if (!currentSimulacrumId.value) return

      if (newStatus === 'in_review') {
        const canSend = await vm.$refs.runner?.validateBeforeReview?.()
        if (!canSend || !canSend.ok) {
          const first = canSend && canSend.missing && canSend.missing[0]
          if (first && vm.$toast) {
            vm.$toast.error(`Falta: ${first.section}${first.subsection ? ' / ' + first.subsection : ''} - ${first.name}`)
          }
          return
        }
      }

      try {
        loading.value = true
        const payload = { status: newStatus }
        if (toUserId) payload.id_user = toUserId
        await axios.post(`/v1/simulacrums/update_status/${currentSimulacrumId.value}`, payload)
        currentState.value = newStatus
        vm.$toast?.success?.(`${tf('custom_forms.state', 'Estado')}: ${labelForState(newStatus.toLowerCase())}`)
        await loadSimulacrumMeta(currentSimulacrumId.value)
        await ensureAnswerExists()
        vm.$nextTick(() => vm.$refs.runner?.reloadForm?.())
      } catch (err) {
        vm.$toast?.error?.(tf('custom_forms.update_state_error', 'No se pudo actualizar el estado'))
      } finally {
        loading.value = false
      }
    }

    const waitForRunnerIdle = (runner, timeoutMs = 60000) => {
      return new Promise((resolve) => {
        try {
          if (!runner || runner.loading === false) return resolve()
          let settled = false
          const done = () => {
            if (!settled) {
              settled = true
              if (typeof unwatch === 'function') unwatch()
              clearTimeout(tid)
              resolve()
            }
          }
          const unwatch = runner.$watch('loading', (val) => {
            if (val === false) done()
          })
          const tid = setTimeout(done, Math.max(1000, Number(timeoutMs) || 60000))
        } catch (_) {
          resolve()
        }
      })
    }

    const onClickSave = async () => {
      if (!currentSimulacrumId.value) return
      try {
        saving.value = true
        const waitForSubmit = new Promise((resolve) => {
          _pendingSaveResolve.value = resolve
        })
        const safetyTimeout = new Promise((resolve) => setTimeout(resolve, 700))
        let triggered = false
        if (vm.$refs && vm.$refs.runner) {
          if (typeof vm.$refs.runner.submitForm === 'function') {
            vm.$refs.runner.submitForm()
            triggered = true
          } else if (vm.$refs.runner.$refs?.form?.submit) {
            vm.$refs.runner.$refs.form.submit()
            triggered = true
          }
        }
        if (!triggered && typeof _pendingSaveResolve.value === 'function') {
          _pendingSaveResolve.value()
          _pendingSaveResolve.value = null
        }
        const runner = vm.$refs?.runner || null
        const waitRunner = runner ? waitForRunnerIdle(runner, 15000) : Promise.resolve()
        await Promise.race([waitForSubmit, waitRunner, safetyTimeout])
        didBootstrap.value = false
        await loadSimulacrumMeta(currentSimulacrumId.value)
        await ensureAnswerExists()
        vm.$nextTick(() => vm.$refs.runner?.reloadForm?.())

        const msg = tf('custom_forms.answers_saved', 'Respuestas guardadas')
        vm.$toast && vm.$toast({
          component: ToastificationContent,
          props: { title: msg, icon: 'CheckIcon', variant: 'success' },
        })
      } catch (err) {
        _pendingSaveResolve.value = null
        vm.$toast && vm.$toast.error(tf('risk.save_failed', 'No se pudo guardar'))
      } finally {
        _pendingSaveResolve.value = null
        saving.value = false
      }
    }

    const fetchSimulacrum = async () => {
      try {
        const idUnit = props.data?.unit_id || props.data?.unit?.id
        const idSim = props.data?.simulacrum_id || props.data?.id

        if (idSim) {
          currentSimulacrumId.value = idSim
          await loadSimulacrumMeta(idSim)
          await ensureAnswerExists()
          readyToRenderForm.value = true
        } else if (idUnit) {
          const params = { params: { id_unit: idUnit } }
          const response = await axios.get('/v1/simulacrums', params)
          const list = response.data.data || []
          if (list.length) {
            currentSimulacrumId.value = list[0].id
            await loadSimulacrumMeta(currentSimulacrumId.value)
            await ensureAnswerExists()
            readyToRenderForm.value = true
          }
        }
      } catch (_) {
      } finally {
        loading.value = false
      }
    }

    const onAnswersSubmit = () => {
      if (typeof _pendingSaveResolve.value === 'function') {
        _pendingSaveResolve.value()
        _pendingSaveResolve.value = null
      }
      loading.value = false
    }

    const onAnswersSubmitError = () => {
      if (typeof _pendingSaveResolve.value === 'function') {
        _pendingSaveResolve.value()
        _pendingSaveResolve.value = null
      }
      loading.value = false
      vm.$toast && vm.$toast.error(tf('risk.save_failed', 'No se pudo guardar'))
    }

    onMounted(async () => {
      await Promise.all([fetchSimulacrum(), loadReviewerOptions(), loadReassignableOptions()])
    })

    return {
      loading, saving, currentSimulacrumId, inlineSnapshot, inlineSources, referenceNumber, unitNameDisplay, rems, sirh, currentState, templateNames, statuses,
      readyToRenderForm, runnerKey, showAllHistory, limitHistory, selectedReviewer, selectedReassign, userOptions, reassignableOptions,
      canSendToReview, canEditForm, canReviewSimulacrum, showReviewActions, canReassignAfterRejected, canShowReassign,
      sortedHistory, visibleHistory, hasHistoryComments, historyGridStyle, labelForState, badgeFor, formatDate, getUserName, getUserEmail, formatUser, getStatusComment,
      updateSimulacrumStatus, onAnswersSubmit, onAnswersSubmitError, tf, tc, showSaveButton, onClickSave, templateSnapshot, templateSourceIds,
    }
  },
}
</script>

<style scoped>
.simulacrum-slot {
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 0;
}
.slot-header {
  flex: 0 0 auto;
}
.slot-body {
  flex: 1 1 0;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.info-card {
  background: #f9fafb;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 10px 12px;
}
.info-row {
  display: flex;
  gap: 6px;
  margin-bottom: 4px;
}
.info-label {
  font-weight: 600;
  color: #444;
}
.info-value {
  color: #111;
}

.history-card {
  background: #fff;
  border: 1px solid #eceff3;
  border-radius: 8px;
  padding: 12px;
  overflow: hidden;
}
.history-header {
  margin-bottom: 8px;
}
.history-table {
  width: 100%;
}
.history-row {
  display: grid;
  column-gap: 8px;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px dashed #eee;
}
.history-row:last-child {
  border-bottom: 0;
}
.history-row--head {
  background: #fff;
  border-bottom: 1px solid #e9ecef;
  font-weight: 600;
  color: #444;
  padding-top: 0;
  padding-bottom: 8px;
}
.col-state {
  justify-self: start;
}
.col-user {
  justify-self: start;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.col-date {
  justify-self: end;
  white-space: nowrap;
}
.col-note {
  white-space: normal;
  overflow: hidden;
}
.history-badge {
  display: inline-flex;
  align-items: center;
}
.history-date {
  font-size: 12px;
  color: #6c757d;
  font-variant-numeric: tabular-nums;
}
.history-user {
  display: flex;
  flex-direction: column;
  min-width: 0;
}
.history-user .user-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.history-user .user-email {
  font-size: 12px;
  color: #6c757d;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.reviewer-inline {
  flex: 1 1 240px;
  min-width: 220px;
  max-width: 520px;
}
.reviewer-inline :deep(.vs__dropdown-toggle) {
  width: 100%;
  min-height: 38px;
}
</style>

<style>
.vs__dropdown-menu {
  z-index: 2055 !important;
}
.select-opens-up .vs__dropdown-menu {
  top: auto !important;
  bottom: 100% !important;
  margin-bottom: 4px;
}
</style>
